import { useEffect, useState, useCallback, useMemo } from "react";
import { Layout } from "../";
import { Loader, GameContextSelector } from "../../components";
import { Container } from "./styled";
import { useHistory } from "react-router";
import { calculatePercj, calculatePlayerStats } from "../../lib/cabbStats";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const STAT_DESCRIPTIONS = {
  PPP: "Mide la cantidad de puntos que genera un equipo o jugador por cada posesión del balón.",
  eFG: "Es una métrica de evaluación de jugadores o equipos que mide la efectividad de los tiros de 2 y 3 puntos.",
  FTR: "Proporción de tiros libres intentados según el número de tiros realizados.",
  OFE: "Cuántos puntos anota un jugador o un equipo por cada 100 posesiones.",
  DFE: "Cuántos puntos permite un jugador o un equipo por cada 100 posesiones.",
  NETEFF: "Net efficiency",
  TOR: "Proporción de tiros libres intentados según el número de tiros realizados.",
  OREB: "Capacidad de un equipo para conseguir rebotes ofensivos.",
  DREB: "Capacidad de un equipo para conseguir rebotes defensivos.",
  POSS: "Estima las posesiones basándose tanto en las estadísticas del equipo como en las del oponente",
  ASSR: "Mide el porcentaje de posesiones que terminan en asistencia.",
  TSP: "",
  PTS: "Puntos marcados por partido.",
  OppPTS: "Puntos permitidos por partido.",
  REB: "Rebotes totales conseguidos por partido",
  ORB: "Rebotes ofensivos conseguidos por partido",
  DRB: "Rebotes defensivos conseguidos por partido",
  ASS: "Asistencias generadas por partido",
  STL: "Robos por partido",
  BLK: "Tapones por partido",
  FL: "Faltas cometidas por partido",
  TO: "Perdidas cometidas por partido",
  FGA: "Lanzamientos de campo por partido",
  FG: "Lanzamientos de campo por partido",
  FGP: "Porcentaje de tiros de campo anotados",
  THREEPA: "Lanzamientos de triple por partido",
  THREEP: "Lanzamientos de triple anotados por partido",
  THREEPP: "Porcentaje de tiros de triple anotados",
  TWOOPA: "Lanzamientos de doble por partido",
  TWOOP: "Lanzamientos de doble anotados por partido",
  TWOOPP: "Porcentaje de tiros de doble anotados",
  FTA: "Lanzamientos de tiro libre por partido",
  FT: "Lanzamientos de tiro libre anotados por partido",
  FTP: "Porcentaje de tiros libres anotados",
};

const replaceNaNValues = (data, replacement = 0) => {
  return data.map((obj) => ({
    ...obj,
    value:
      obj.value === null ||
      isNaN(obj.value) ||
      obj.value === Number.POSITIVE_INFINITY
        ? replacement
        : obj.value,
  }));
};

const sortByValue = (arr, statInX) => {
  return statInX === "DFE"
    ? arr.sort((a, b) => a.value - b.value)
    : arr.sort((a, b) => b.value - a.value);
};

const formatDateToDDMMYYYY = (dateObj) => {
  if (!dateObj) return null;
  const date = new Date(dateObj);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseDDMMYYYYToDate = (dateString) => {
  if (!dateString || dateString === "active") return null;
  const [day, month, year] = dateString.split("/");
  return new Date(year, month - 1, day);
};

const CabbStats = ({ appData }) => {
  const [selectedChart, setSelectedChart] = useState(null);
  const [labels, setLabels] = useState([]);
  const [dataSet, setDataSet] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [gameContextData, setGameContextData] = useState({
    activeLeague: null,
    activePhase: null,
    activeSeason: null,
    startDate: null,
    endDate: null,
    activeTeam: null,
    activeRival: null,
    baseTeam: null,
    games: null,
    statInX: null,
    isLoading: false,
  });

  const history = useHistory();

  const chartOptions = useMemo(
    () => ({
      indexAxis: "y",
      normalized: true,
      elements: {
        bar: {
          borderWidth: 1,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: "right",
        },
        title: {
          display: false,
        },
      },
      scales: {
        y: {
          ticks: {
            font: (context) => {
              const width = context.chart.width;
              const size = width >= 867 ? 12 : 6;
              return { size };
            },
          },
        },
      },
    }),
    []
  );

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: dataSet,
          borderColor: "rgb(255, 99, 132)",
          backgroundColor: "rgba(255, 99, 132, 0.5)",
        },
      ],
    }),
    [labels, dataSet]
  );

  const processTeamsData = useCallback((games, teams, league, statInX) => {
    const teamsData =
      teams
        ?.filter((team) => team.league === league)
        .map((team) => ({
          _id: team._id,
          name: team.name,
          value: isNaN(calculatePercj(games, team._id, statInX))
            ? 0
            : calculatePercj(games, team._id, statInX),
        })) || [];

    return sortByValue(teamsData, statInX);
  }, []);

  const processPlayersData = useCallback(
    (games, teams, players, league, statInX, contextData) => {
      const teamIds =
        teams
          ?.filter((team) => team.league === league)
          .map((team) => team._id) || [];

      const playersData =
        players
          ?.filter((player) => {
            if (!player.teams_history || !Array.isArray(player.teams_history)) {
              return false;
            }

            return player.teams_history.some((item) => {
              // Check if season matches
              const seasonMatches = item.season === contextData.activeSeason;

              // Check if team is in the league
              const teamInLeague = teamIds.includes(item.teamId);

              // Check if player was active during the period
              let isActiveDuringPeriod = false;

              if (contextData.startDate && contextData.endDate) {
                const contextStartDateFormatted = formatDateToDDMMYYYY(
                  contextData.startDate
                );
                const contextEndDateFormatted = formatDateToDDMMYYYY(
                  contextData.endDate
                );

                // Parse player dates from DD/MM/YYYY to Date objects for comparison
                const playerStartDate = parseDDMMYYYYToDate(item.start_date);
                const playerEndDate =
                  item.end_date === "active" || item.end_date === undefined
                    ? new Date()
                    : parseDDMMYYYYToDate(item.end_date);

                const contextStartDate = parseDDMMYYYYToDate(
                  contextStartDateFormatted
                );
                const contextEndDate = parseDDMMYYYYToDate(
                  contextEndDateFormatted
                );

                // Check if periods overlap
                if (playerStartDate && contextStartDate && contextEndDate) {
                  isActiveDuringPeriod =
                    playerStartDate <= contextEndDate &&
                    playerEndDate >= contextStartDate;
                } else {
                  // Fallback to active status if dates are missing
                  isActiveDuringPeriod =
                    item.end_date === "active" || item.end_date === undefined;
                }
              } else {
                // If no date range, check if player was active or end_date is undefined
                isActiveDuringPeriod =
                  item.end_date === "active" || item.end_date === undefined;
              }

              return seasonMatches && teamInLeague && isActiveDuringPeriod;
            });
          })
          .map((player) => ({
            _id: player._id,
            name: `${player.player_name} ${player.player_lastname}`,
            value: calculatePlayerStats(games, player._id, statInX),
          })) || [];

      const cleanedData = replaceNaNValues(playersData);
      return sortByValue(cleanedData, statInX);
    },
    []
  );

  const getActiveGames = useCallback((games, contextData) => {
    if (!games) return [];

    return games.filter((game) => {
      const matchesLeague = game.category === contextData.activeLeague;

      if (contextData.startDate && contextData.endDate) {
        return matchesLeague;
      }

      if (contextData.activeSeason) {
        const matchesSeason = game.season === contextData.activeSeason;
        const matchesPhase =
          !contextData.activePhase || game.stage === contextData.activePhase;
        return matchesLeague && matchesSeason && matchesPhase;
      }

      return false;
    });
  }, []);

  // Handle GameContextSelector changes
  const handleGameContextChange = useCallback((contextData) => {
    setGameContextData(contextData);
  }, []);

  const handleTeamsClick = useCallback(() => {
    setSelectedChart("teams");
    setLabels([]);
    setDataSet([]);
  }, []);

  const handlePlayersClick = useCallback(() => {
    setSelectedChart("players");
    setLabels([]);
    setDataSet([]);
  }, []);

  const handleChartClick = useCallback(
    (dataset) => {
      if (!dataset.length) return;

      const clickedLabel = chartData.labels[dataset[0].index];

      if (selectedChart === "players") {
        const player = appData.players.find(
          (p) => `${p.player_name} ${p.player_lastname}` === clickedLabel
        );
        if (player) {
          history.push({ pathname: `statsProfile/${player._id}` }, "/");
        }
      } else {
        const team = appData.teams.find((t) => t.name === clickedLabel);
        if (team) {
          history.push({ pathname: `teamProfile/${team._id}` }, "/");
        }
      }
    },
    [selectedChart, chartData.labels, appData.players, appData.teams, history]
  );

  useEffect(() => {
    if (
      !gameContextData.games ||
      !gameContextData.activeLeague ||
      !gameContextData.statInX
    ) {
      return;
    }

    const activeGames = getActiveGames(gameContextData.games, gameContextData);

    if (activeGames.length === 0) {
      setLabels([]);
      setDataSet([]);
      return;
    }

    let processedData = [];

    if (selectedChart === "players") {
      processedData = processPlayersData(
        activeGames,
        appData.teams,
        appData.players,
        gameContextData.activeLeague,
        gameContextData.statInX,
        gameContextData
      );
    } else {
      processedData = processTeamsData(
        activeGames,
        appData.teams,
        gameContextData.activeLeague,
        gameContextData.statInX
      );
    }

    const newLabels = processedData.map((item) => item.name);
    const newDataSet = processedData.map((item) => item.value);

    setLabels(newLabels);
    setDataSet(newDataSet);
  }, [
    gameContextData,
    selectedChart,
    appData.teams,
    appData.players,
    getActiveGames,
    processTeamsData,
    processPlayersData,
  ]);

  useEffect(() => {
    if (appData) setIsLoaded(true);
  }, [appData]);

  const currentDescription = STAT_DESCRIPTIONS[gameContextData.statInX] || "";

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <Layout isPositionUnset={true}>
      <Container>
        <div className="header">
          <h1>
            {selectedChart === "players"
              ? "Estadísticas por jugadores"
              : "Estadísticas por equipos"}
          </h1>
          <div className="chart-options_container">
            <div
              className={`chart-option ${
                selectedChart !== "players" ? "active" : ""
              }`}
            >
              <button onClick={handleTeamsClick}>Equipos</button>
            </div>
            <div
              className={`chart-option ${
                selectedChart === "players" ? "active" : ""
              }`}
            >
              <button onClick={handlePlayersClick}>Jugadores</button>
            </div>
          </div>
          <GameContextSelector
            appData={appData}
            typeOfSelector="statistics"
            onChange={handleGameContextChange}
          />
        </div>
        <div
          className={`chart-container ${
            selectedChart !== "players" ? "teams" : "players"
          }`}
        >
          <Bar
            options={chartOptions}
            data={chartData}
            height={1}
            getElementAtEvent={handleChartClick}
          />
        </div>
        <p className="description">
          {currentDescription} <a href="/glosary">Mas información</a>
        </p>
      </Container>
    </Layout>
  );
};

export default CabbStats;
